import { useEffect, useState } from 'react';

/**
 * The current time as reactive state, for a component that renders a date or a countdown.
 *
 * Reading `Date.now()` during render is impure, and with the React Compiler on it is worse than
 * stale: the compiler caches the value in the component's memo slot, so it is read ONCE per mount
 * and never again — a patient file left open showed the day it was opened on, indefinitely, and a
 * refetch could not heal it. `useState(() => Date.now())` moves the freeze into state rather than
 * removing it; the interval is what makes the value live. `SnoozeTaskModal` deliberately keeps the
 * frozen form: a modal wants one clock read per open, not a ceiling that shifts under the user.
 */
export default function useNow(intervalMs = 60_000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const tick = () => setNow(Date.now());
    const id = window.setInterval(tick, intervalMs);
    // A backgrounded tab throttles timers, so catch up the moment it comes back.
    const onVisible = () => { if (document.visibilityState === 'visible') tick(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.clearInterval(id);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [intervalMs]);
  return now;
}
